// Document tables tab: the table list (caption, page, size, source clause, digitised curves) with keyset paging, and the selected table rendered with a sticky header.
import { useInfiniteQuery } from "@tanstack/react-query";
import { Table2Icon } from "lucide-react";
import { useState } from "react";

import type { TableRow } from "@/api/__generated__";
import { catalogListTablesInfiniteOptions } from "@/api/__generated__/@tanstack/react-query.gen";
import { ClauseLink } from "@/components/common/ClauseLink";
import { EmptyState } from "@/components/common/EmptyState";
import { ProblemState } from "@/components/common/ProblemState";
import { Pane, SplitPane } from "@/components/common/SplitPane";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { TableView } from "@/features/library/TableView";
import { cn } from "@/lib/cn";
import { formatNumber, plural } from "@/lib/format";

const PAGE_SIZE = 50;

export function tableCaption(table: TableRow): string {
  return table.caption ?? `Table on page ${String(table.page)}`;
}

export function TablesTab({ docUid }: { docUid: string }) {
  const request = { path: { doc_uid: docUid }, query: { limit: PAGE_SIZE } };
  const tables = useInfiniteQuery({
    ...catalogListTablesInfiniteOptions(request),
    initialPageParam: request,
    getNextPageParam: (page) => page.next_cursor ?? undefined,
  });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const items = tables.data?.pages.flatMap((page) => page.items) ?? [];
  const total = tables.data?.pages[0]?.total ?? 0;
  const selected = items.find((table) => table.key === selectedKey) ?? items[0];

  if (tables.isError) {
    return (
      <Card>
        <ProblemState
          problem={tables.error}
          retry={() => {
            void tables.refetch();
          }}
        />
      </Card>
    );
  }
  if (!tables.isPending && items.length === 0) {
    return (
      <Card>
        <EmptyState
          icon={Table2Icon}
          title="This document has no tables"
          description="No table was found in the source PDF during ingestion."
        />
      </Card>
    );
  }
  return (
    <Card className="h-[max(520px,calc(100dvh-var(--topbar-height)-230px))] overflow-hidden">
      <SplitPane className="grid-cols-[minmax(240px,340px)_minmax(0,1fr)]">
        <Pane as="nav" label="Tables" border="right" className="bg-subtle">
          <p className="m-0 border-b px-3.5 py-2.5 text-xs text-muted-foreground">
            {tables.isPending ? "Loading tables…" : plural(total, "table")}
          </p>
          {tables.isPending ? (
            <div aria-hidden className="flex flex-col gap-2 p-3">
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton key={index} className="h-11" />
              ))}
            </div>
          ) : (
            <ul className="m-0 list-none p-1.5">
              {items.map((table) => {
                const active = table.key === selected?.key;
                return (
                  <li key={table.table_uid}>
                    <button
                      type="button"
                      aria-current={active ? "true" : undefined}
                      onClick={() => {
                        setSelectedKey(table.key);
                      }}
                      className={cn(
                        "flex w-full flex-col gap-0.5 rounded-sm border-0 bg-transparent px-2.5 py-2 text-left hover:bg-accent",
                        active && "bg-selected text-selected-foreground hover:bg-selected",
                      )}
                    >
                      <span className="truncate text-ui font-medium">{tableCaption(table)}</span>
                      <span className="text-xs text-muted-foreground tabular-nums">
                        p. {table.page} · {formatNumber(table.n_rows)} × {formatNumber(table.n_cols)}
                        {table.clause_number ? ` · ${table.clause_number}` : ""}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {tables.hasNextPage ? (
            <div className="p-3">
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                disabled={tables.isFetchingNextPage}
                onClick={() => {
                  void tables.fetchNextPage();
                }}
              >
                {tables.isFetchingNextPage ? "Loading…" : "Show more"}
              </Button>
            </div>
          ) : null}
        </Pane>
        <Pane label="Table">
          {selected ? (
            <div className="flex min-h-0 flex-1 flex-col gap-3 p-5">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="m-0 text-section font-semibold text-foreground">{tableCaption(selected)}</h2>
                <Badge variant="outline">p. {selected.page}</Badge>
                <Badge variant="outline">
                  {formatNumber(selected.n_rows)} rows · {formatNumber(selected.n_cols)} columns
                </Badge>
                {selected.curve_count > 0 ? (
                  <Badge variant="brand">{plural(selected.curve_count, "digitised curve")}</Badge>
                ) : null}
                {selected.clause_uid ? (
                  <span className="text-xs text-muted-foreground">
                    in <ClauseLink clauseUid={selected.clause_uid} number={selected.clause_number} />
                  </span>
                ) : null}
              </div>
              <TableView
                key={selected.key}
                docUid={docUid}
                tableKey={selected.key}
                caption={tableCaption(selected)}
                className="min-h-0 flex-1"
              />
            </div>
          ) : (
            <div aria-hidden className="flex flex-col gap-2 p-5">
              <Skeleton className="h-6 w-1/3" />
              <Skeleton className="h-64 w-full" />
            </div>
          )}
        </Pane>
      </SplitPane>
    </Card>
  );
}
